/**
 * Forgot-password page (shared for students + teachers).
 *
 * The `scope` prop tells us which backend endpoint to hit and where the
 * matching login page is.
 */
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api";

export default function ForgotPassword({ scope }) {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const isTeacher = scope === "teacher";
  const loginPath = isTeacher ? "/teachers" : "/login";

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const call = isTeacher ? api.teacherForgotPassword : api.studentForgotPassword;
      await call(email.trim().toLowerCase());
      setSubmitted(true);
    } catch (err) {
      // Server always returns 200, so this only triggers on network / 4xx validation
      setError(err.message || "Could not submit the request.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main
      className="min-h-[100dvh] bg-[#FAF9F6] pt-24 md:pt-32 pb-12 px-6"
      data-testid="forgot-password-page"
    >
      <div className="max-w-md mx-auto">
        <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60 mb-4">
          {isTeacher ? "Teachers" : "Students"}
        </div>
        <h1 className="font-display text-4xl md:text-5xl leading-tight tracking-tighter font-light text-[#1C1C1C]">
          Reset your password.
        </h1>

        {!submitted ? (
          <>
            <p className="mt-4 text-base text-[#1C1C1C]/70 leading-relaxed">
              Enter the email address associated with your account. If we find it,
              we'll send you a link to choose a new password. The link expires in 60 minutes.
            </p>
            <form onSubmit={onSubmit} className="mt-10 space-y-4">
              <Field label="Email">
                <input
                  data-testid="forgot-password-email"
                  autoFocus
                  required
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input"
                />
              </Field>
              {error && (
                <div className="text-sm text-[#B83A3A]" data-testid="forgot-password-error">{error}</div>
              )}
              <button
                data-testid="forgot-password-submit"
                type="submit"
                disabled={loading || !email}
                className="w-full px-5 py-3 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-sm tracking-[0.16em] uppercase font-medium hover:bg-[#1C1C1C]/85 disabled:opacity-40 transition-colors"
              >
                {loading ? "Sending…" : "Send reset link"}
              </button>
              <div className="text-center">
                <Link
                  to={loginPath}
                  className="text-[11px] tracking-[0.28em] uppercase font-medium text-[#1C1C1C]/55 hover:text-[#1C1C1C]"
                >
                  ← Back to sign in
                </Link>
              </div>
            </form>
          </>
        ) : (
          <div
            className="mt-10 bg-white border border-[#1C1C1C]/10 rounded-2xl p-8"
            data-testid="forgot-password-confirmation"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-800 text-[10px] tracking-[0.22em] uppercase font-medium mb-4">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Request received
            </div>
            <p className="text-[#1C1C1C]/85 leading-relaxed">
              If an account exists for <span className="font-medium text-[#1C1C1C]">{email}</span>,
              a password-reset link is on its way. Check your inbox (and spam folder) in the next few minutes.
            </p>
            <p className="text-sm text-[#1C1C1C]/55 mt-4 leading-relaxed">
              Didn't use this email? You can safely close this page — nothing has changed.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate(loginPath)}
                data-testid="forgot-password-back-to-login"
                className="px-5 py-3 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-xs tracking-[0.18em] uppercase font-medium hover:bg-[#1C1C1C]/85 transition-colors"
              >
                Back to sign in
              </button>
              <button
                onClick={() => { setSubmitted(false); setEmail(""); }}
                className="px-5 py-3 bg-white border border-[#1C1C1C]/15 text-[#1C1C1C] rounded-xl text-xs tracking-[0.18em] uppercase font-medium hover:border-[#1C1C1C]/40 transition-colors"
              >
                Try a different email
              </button>
            </div>
          </div>
        )}

        <style>{`.input{width:100%;padding:.75rem 1rem;background:white;border:1px solid rgba(28,28,28,0.1);border-radius:.75rem;font-size:1rem;outline:none}.input:focus{border-color:rgba(28,28,28,0.4)}`}</style>
      </div>
    </main>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.28em] uppercase font-medium text-[#1C1C1C]/60 mb-2">{label}</div>
      {children}
    </label>
  );
}
